/**
 * PSMF document model — EU GVP Module II Section II.B.4 main body +
 * Section II.B.6.1 Annex A-I structure. Annex keys are fixed and cannot
 * be renumbered; absent content is marked `unused: true`.
 */

export type PsmfStatus = 'DRAFT' | 'FINALISED';

export interface PsmfLogEntry {
  id: string;
  timestamp: string; // ISO
  user: string; // placeholder — replace with session user once backend lands
  field: string;
  summary: string;
}

export interface CoverPage {
  mahName: string;
  qppvName: string; // required
  qppvEmail: string;
  qppvPhone: string;
  qppvAltContact: string;
  evPsmfRef: string; // Article 57 / XEVMPD reference
  preparedAt: string; // ISO date (YYYY-MM-DD)
  lastUpdatedAt: string;
  versionNumber: string;
  location: string; // PSMF physical location (EU/EEA)
  language: string;
}

// II.B.4.1 — QPPV
export interface MainQppv {
  deputyName: string;
  deputyContact: string;
  delegationSummary: string;
  notificationTriggers: string;
}

// II.B.4.2 — Organisational structure
export interface MainOrganisation {
  chartDescription: string;
  reportingLines: string;
  interfaces: string; // Medical Affairs, Regulatory, Quality
  sites: string;
}

// II.B.4.3 — Sources of safety data
export interface MainSources {
  spontaneous: boolean;
  clinicalTrials: boolean;
  literature: boolean;
  regulatoryAuthorities: boolean;
  partners: boolean;
  patientSupportProgrammes: boolean;
  socialMedia: boolean;
  notes: string;
}

// II.B.4.4 — Computerised systems
export interface MainSystems {
  safetyDbName: string;
  safetyDbVersion: string;
  validationStatus: string; // GAMP 5 / IQ-OQ-PQ summary
  fitnessForPurpose: string;
  changeControl: string;
  backup: string;
}

// II.B.4.5 — PV processes
export interface MainProcesses {
  icsrProcess: string;
  signalDetection: string;
  psurProcess: string;
  riskBenefit: string;
  safetyCommunication: string;
  smpcVariations: string;
}

// II.B.4.6 — Performance
export interface MainPerformance {
  kpiSummary: string;
  complianceRate: string;
  variationTracking: string;
}

// II.B.4.7 — Quality system
export interface MainQuality {
  qualitySystem: string;
  training: string;
  auditProgramme: string;
  deviationManagement: string;
}

export interface MainBody {
  qppv: MainQppv;
  organisation: MainOrganisation;
  sources: MainSources;
  systems: MainSystems;
  processes: MainProcesses;
  performance: MainPerformance;
  quality: MainQuality;
}

// Annex rows -------------------------------------------------------------

export interface AnnexADelegation {
  id: string;
  task: string;
  delegatedTo: string;
  oversight: string;
}

export interface AnnexAContact {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
}

export interface AnnexBContract {
  id: string;
  counterparty: string;
  type: string; // SDEA, MSA, sublicensing...
  scope: string;
  effectiveDate: string;
  status: string;
}

export interface AnnexCSource {
  id: string;
  kind: string; // affiliate | study | programme | third-party
  name: string;
  country: string;
  startDate: string;
  endDate: string;
}

export interface AnnexDSystem {
  id: string;
  name: string;
  version: string;
  location: string;
  operationalOwner: string;
  fitnessForPurpose: string;
  validationStatus: string;
}

export interface AnnexESop {
  id: string;
  docId: string;
  title: string;
  effectiveDate: string;
  docType: string; // SOP, Work Instruction, Manual, Policy
}

export interface AnnexFKpi {
  id: string;
  name: string;
  target: string;
  currentResult: string;
}

export interface AnnexGAudit {
  id: string;
  scope: string;
  dateOfConduct: string;
  dateOfReport: string;
  significantFindings: string;
  capaStatus: string;
  resolutionDate: string;
}

export interface AnnexHProduct {
  id: string;
  activeSubstance: string;
  inn: string;
  authorisationNumber: string;
  procedureType: string; // Centralised, MRP, DCP, National
  rapporteurOrRms: string;
  marketingStatus: string;
}

export interface AnnexILogEntry {
  id: string;
  annex: string; // A..H
  date: string;
  user: string;
  change: string;
}

export interface AnnexData {
  A: {
    unused: boolean;
    delegations: AnnexADelegation[];
    cvReference: string;
    contacts: AnnexAContact[];
  };
  B: {
    unused: boolean;
    contracts: AnnexBContract[];
  };
  C: {
    unused: boolean;
    sources: AnnexCSource[];
  };
  D: {
    unused: boolean;
    systems: AnnexDSystem[];
  };
  E: {
    unused: boolean;
    sops: AnnexESop[];
  };
  F: {
    unused: boolean;
    kpis: AnnexFKpi[];
    timeliness15Day: string; // placeholder commentary
    timeliness90Day: string;
  };
  G: {
    unused: boolean;
    schedule: string;
    audits: AnnexGAudit[];
  };
  H: {
    unused: boolean;
    products: AnnexHProduct[];
  };
  I: {
    unused: boolean;
    entries: AnnexILogEntry[];
  };
}

export interface Psmf {
  id: string;
  tenantId: string;
  status: PsmfStatus;
  createdAt: string;
  updatedAt: string;
  cover: CoverPage;
  main: MainBody;
  annex: AnnexData;
  logbook: PsmfLogEntry[];
}

export const ANNEX_KEYS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'] as const;
export type AnnexKey = typeof ANNEX_KEYS[number];

export const ANNEX_TITLES: Record<AnnexKey, string> = {
  A: 'QPPV',
  B: 'Organisational structure',
  C: 'Sources of safety data',
  D: 'Computerised systems and databases',
  E: 'PV process and written procedures',
  F: 'PV system performance',
  G: 'Quality system',
  H: 'Products',
  I: 'Document and record control',
};

export const MAIN_SECTIONS = [
  { key: 'qppv', ref: 'II.B.4.1', title: 'QPPV' },
  { key: 'organisation', ref: 'II.B.4.2', title: 'Organisational structure' },
  { key: 'sources', ref: 'II.B.4.3', title: 'Sources of safety data' },
  { key: 'systems', ref: 'II.B.4.4', title: 'Computerised systems' },
  { key: 'processes', ref: 'II.B.4.5', title: 'PV processes' },
  { key: 'performance', ref: 'II.B.4.6', title: 'System performance' },
  { key: 'quality', ref: 'II.B.4.7', title: 'Quality system' },
] as const;

export function emptyPsmf(tenantId: string, id: string): Psmf {
  const now = new Date().toISOString();
  const today = now.slice(0, 10);
  return {
    id,
    tenantId,
    status: 'DRAFT',
    createdAt: now,
    updatedAt: now,
    cover: {
      mahName: '',
      qppvName: '',
      qppvEmail: '',
      qppvPhone: '',
      qppvAltContact: '',
      evPsmfRef: '',
      preparedAt: today,
      lastUpdatedAt: today,
      versionNumber: '1.0',
      location: '',
      language: 'English',
    },
    main: {
      qppv: { deputyName: '', deputyContact: '', delegationSummary: '', notificationTriggers: '' },
      organisation: { chartDescription: '', reportingLines: '', interfaces: '', sites: '' },
      sources: {
        spontaneous: true,
        clinicalTrials: false,
        literature: true,
        regulatoryAuthorities: true,
        partners: false,
        patientSupportProgrammes: false,
        socialMedia: false,
        notes: '',
      },
      systems: {
        safetyDbName: '',
        safetyDbVersion: '',
        validationStatus: '',
        fitnessForPurpose: '',
        changeControl: '',
        backup: '',
      },
      processes: {
        icsrProcess: '',
        signalDetection: '',
        psurProcess: '',
        riskBenefit: '',
        safetyCommunication: '',
        smpcVariations: '',
      },
      performance: { kpiSummary: '', complianceRate: '', variationTracking: '' },
      quality: { qualitySystem: '', training: '', auditProgramme: '', deviationManagement: '' },
    },
    annex: {
      A: { unused: false, delegations: [], cvReference: '', contacts: [] },
      B: { unused: false, contracts: [] },
      C: { unused: false, sources: [] },
      D: { unused: false, systems: [] },
      E: { unused: false, sops: [] },
      F: { unused: false, kpis: [], timeliness15Day: '', timeliness90Day: '' },
      G: { unused: false, schedule: '', audits: [] },
      H: { unused: false, products: [] },
      I: { unused: false, entries: [] },
    },
    logbook: [],
  };
}
